import type { TypeDetail } from "./types";

// Replaces the filler "Overview" paragraph with three structured panels
// derived from the type's existing fields. The audit's #2 critique on
// /c/:cat/:type was that the Overview was seed-data noise — this surfaces
// real, customer-decisive information instead: what arrives at the
// jobsite, what doesn't, and the operational constraints.
export interface TypeHighlightsProps {
  type: TypeDetail;
}

interface PanelItem {
  label: string;
  detail?: string | undefined;
}

function highlightItems(type: TypeDetail): PanelItem[] {
  const items: PanelItem[] = [];
  if (type.delivery_only) {
    items.push({ label: "Delivered to your jobsite", detail: "We bring it; self-pickup isn't offered for this class." });
  } else if (type.pickup_allowed) {
    items.push({ label: "Delivery or self-pickup", detail: "Skip the delivery fee if you pick up the unit yourself." });
  } else {
    items.push({ label: "Delivered to your jobsite" });
  }

  const noticeHours = type.notice_required_hours;
  if (noticeHours <= 24) {
    items.push({ label: "Next-day availability", detail: `Book at least ${noticeHours} hours ahead.` });
  } else {
    const days = Math.ceil(noticeHours / 24);
    items.push({ label: `${days}-day lead time`, detail: `Book at least ${noticeHours} hours ahead so we can route a unit.` });
  }

  if (type.requires_coi) {
    items.push({ label: "Certificate of insurance required", detail: "Upload your COI in checkout or call us to add a waiver." });
  }
  if (type.uses_fuel) {
    items.push({ label: "Fuel-powered", detail: "Return it at the same fuel level it arrived; otherwise we top off and bill at cost." });
  }
  // Phase 5d type-metadata — per-type duration bounds + hour-meter
  // disclosure. The 7-day floor is the global default so only call it
  // out when this type's min has been raised.
  if (type.min_rental_days > 7) {
    items.push({
      label: `${type.min_rental_days}-day minimum rental`,
      detail: `This rental class requires at least a ${type.min_rental_days}-day window.`
    });
  }
  if (type.max_rental_days != null) {
    items.push({
      label: `${type.max_rental_days}-day maximum`,
      detail: `For longer engagements give us a call — we can route the right class.`
    });
  }
  if (type.has_hour_meter) {
    items.push({
      label: "Hour meter on the unit",
      detail: "We record the hour reading at drop-off and pickup; overage hours bill per the contract §5."
    });
  }
  return items;
}

const INCLUDED: string[] = [
  "Delivery within our service area",
  "Pre-delivery safety walk-through",
  "Spec sheet PDF and operator quick-start",
  "After-hours support line during your rental",
];

function notIncluded(type: TypeDetail): string[] {
  const list = [
    "Operator (you operate)",
    "Fuel top-off (you return at delivered level)",
    "Permits or HOA approvals",
  ];
  if (type.damage_waiver_percent > 0) {
    list.push(`Damage waiver — optional add-on (${Math.round(type.damage_waiver_percent * 100)}%)`);
  }
  return list;
}

export function TypeHighlights({ type }: TypeHighlightsProps): JSX.Element {
  const highlights = highlightItems(type);
  const notIn = notIncluded(type);

  return (
    <section className="mt-12" aria-labelledby="highlights-heading">
      <h2
        id="highlights-heading"
        className="font-serif text-xl font-bold italic tracking-tight text-brand-black md:text-2xl"
      >
        What you get
      </h2>
      <span aria-hidden="true" className="mt-2 mb-4 inline-block h-0.5 w-10 bg-brand-red" />

      <div className="grid gap-4 md:grid-cols-3">
        <HighlightPanel title="Highlights" data-testid="highlights-panel">
          <ul className="space-y-2 text-sm text-brand-black">
            {highlights.map((item) => (
              <li key={item.label} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-1 inline-block h-1.5 w-1.5 flex-none rounded-full bg-brand-red" />
                <div>
                  <p className="font-medium">{item.label}</p>
                  {item.detail ? <p className="text-zinc-600">{item.detail}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </HighlightPanel>

        <HighlightPanel title="Included" data-testid="included-panel">
          <ul className="space-y-1.5 text-sm text-brand-black">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-0.5 text-brand-red">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </HighlightPanel>

        <HighlightPanel title="Not included" data-testid="not-included-panel">
          <ul className="space-y-1.5 text-sm text-brand-black">
            {notIn.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-0.5 text-zinc-400">·</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </HighlightPanel>
      </div>
    </section>
  );
}

function HighlightPanel({
  title,
  children,
  ...rest
}: {
  title: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className="rounded-md border border-zinc-200 bg-brand-white p-4"
      {...rest}
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">
        {title}
      </h3>
      {children}
    </div>
  );
}
